/**
 * Implementation record and assessment pack read APIs.
 *
 * @see aihub-service docs/design/implementation-record-redesign.md §4.5
 */

import { apiClient } from "./apiClient";
import {
  mockFetchAssessmentPackDetail,
  mockFetchImplementationRecordGroups,
  mockFetchImplementationRecords,
} from "./implementationRecordMockData";

/**
 * Reads call the live implementation-record APIs.
 * Set `useMock` / `useListMock` only in tests that need the local fixtures.
 */
export const implementationRecordConfig = {
  useMock: false,
  useListMock: false,
};

export const IMPLEMENTATION_RECORDS_DEFAULT_PAGE_SIZE = 10;
export const IMPLEMENTATION_RECORDS_PACK_PAGE_SIZE = 100;
export const IMPLEMENTATION_RECORDS_PACK_MAX_PAGES = 5;

export type ImplementationAssessmentSource =
  | "classification"
  | "conditional_classification"
  | "assessment";

export type ImplementationObjectRef = {
  objectType: string;
  objectSubType: string;
  objectTypeName: string;
  objectSubTypeName: string;
  id: string;
  name: string;
  version: string | null;
};

export type AssessmentRecordItemValue = {
  code: string;
  name: string;
};

/** One concept written back to the assessment record when impact is Y. */
export type AssessmentRecordItem = {
  concept: string;
  displayName: string;
  allValues: boolean;
  values: AssessmentRecordItemValue[];
};

export type ImplementationRecord = {
  id: string;
  planId: string;
  assessmentSource: ImplementationAssessmentSource | string;
  assessmentName: string;
  assessor: string;
  assessorName: string;
  referenceObject: ImplementationObjectRef;
  impactedObject: ImplementationObjectRef;
  impacted: boolean;
  impactedLevel: string | null;
  assessmentSummary: string | null;
  detailedAnalysis: string | null;
  recommendation: string | null;
  /** API `assessment_record_items`. */
  assessmentRecordItems: AssessmentRecordItem[];
  assessedAt: string;
};

export type ImplementationQueryScalar = string | readonly string[];

export type ImplementationRecordListQuery = {
  planId?: ImplementationQueryScalar;
  assessmentSource?: ImplementationQueryScalar;
  assessmentName?: ImplementationQueryScalar;
  referenceObjectType?: ImplementationQueryScalar;
  referenceObjectId?: string;
  referenceObjectVersion?: string;
  impacted?: boolean | readonly boolean[];
  impactedObjectType?: ImplementationQueryScalar;
  impactedObjectId?: string;
  keyword?: string;
  assessedFrom?: string;
  assessedTo?: string;
  page?: number;
  pageSize?: number;
};

export function queryScalarList(value?: ImplementationQueryScalar): string[] {
  if (value == null) return [];
  const list = Array.isArray(value) ? value : [value];
  return list.map((item) => item.trim()).filter(Boolean);
}

export function queryBooleanList(
  value?: boolean | readonly boolean[],
): boolean[] {
  if (value == null) return [];
  return Array.isArray(value) ? [...value] : [value];
}

export type ImplementationPaginatedResult<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

export type AssessmentPackImpactedType = {
  objectType: string;
  objectSubType: string;
  displayName: string;
};

export type AssessmentPack = {
  name: string;
  referenceObject: ImplementationObjectRef;
  impactedObjectTypes: AssessmentPackImpactedType[];
  /**
   * Placeholder until pack detail exposes user groups.
   * Mock fills sample labels; a live payload is read when present, otherwise [].
   */
  impactedUserGroups: string[];
};

export type ImplementationFilterOption = {
  value: string;
  label: string;
};

export type ImplementationRecordFilterOptions = {
  assessmentSources: ImplementationFilterOption[];
  assessmentNames: ImplementationFilterOption[];
  impactedObjectTypes: ImplementationFilterOption[];
};

export const EMPTY_IMPLEMENTATION_RECORD_FILTER_OPTIONS: ImplementationRecordFilterOptions =
  {
    assessmentSources: [],
    assessmentNames: [],
    impactedObjectTypes: [],
  };

export type ImplementationRecordGroup = {
  assessmentName: string;
  assessmentSource: string;
  recordCount: number;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readString(
  record: Record<string, unknown>,
  ...keys: string[]
): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }
  return "";
}

function readStringOrNull(
  record: Record<string, unknown>,
  ...keys: string[]
): string | null {
  for (const key of keys) {
    const value = record[key];
    if (value === null) return null;
    if (typeof value === "string") {
      const trimmed = value.trim();
      return trimmed || null;
    }
  }
  return null;
}

function readBoolean(
  record: Record<string, unknown>,
  ...keys: string[]
): boolean {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "boolean") return value;
    if (value === "true" || value === "1") return true;
    if (value === "false" || value === "0") return false;
  }
  return false;
}

function readNumber(
  record: Record<string, unknown>,
  ...keys: string[]
): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function assertSuccess(result: {
  status?: string;
  message?: string | null;
}): void {
  if (result.status !== "success") {
    throw new Error(result.message?.trim() || "Request failed.");
  }
}

function extractListPayload(data: unknown): {
  items: unknown[];
  groups?: unknown[];
  total?: number;
  page?: number;
  pageSize?: number;
} {
  if (Array.isArray(data)) {
    return { items: data };
  }
  const record = asRecord(data);
  if (!record) return { items: [] };

  const nested = asRecord(record.data);
  const itemsCandidate =
    record.items ??
    nested?.items ??
    (Array.isArray(record.data) ? record.data : undefined);
  const groupsCandidate = record.groups ?? nested?.groups;

  return {
    items: Array.isArray(itemsCandidate) ? itemsCandidate : [],
    groups: Array.isArray(groupsCandidate) ? groupsCandidate : undefined,
    total: readNumber(record, "total") ?? (nested ? readNumber(nested, "total") : undefined),
    page: readNumber(record, "page") ?? (nested ? readNumber(nested, "page") : undefined),
    pageSize:
      readNumber(record, "page_size", "pageSize") ??
      (nested ? readNumber(nested, "page_size", "pageSize") : undefined),
  };
}

function normalizeAssessmentRecordItemValue(
  raw: unknown,
): AssessmentRecordItemValue | null {
  const record = asRecord(raw);
  if (!record) return null;
  const code = readString(record, "code");
  const name = readString(record, "name");
  if (!code && !name) return null;
  return { code, name };
}

function normalizeAssessmentRecordItems(raw: unknown): AssessmentRecordItem[] {
  if (!Array.isArray(raw)) return [];
  const items: AssessmentRecordItem[] = [];
  for (const entry of raw) {
    const record = asRecord(entry);
    if (!record) continue;
    const valuesRaw = record.values;
    const values = Array.isArray(valuesRaw)
      ? valuesRaw
          .map((value) => normalizeAssessmentRecordItemValue(value))
          .filter((value): value is AssessmentRecordItemValue => value !== null)
      : [];
    items.push({
      concept: readString(record, "concept"),
      displayName: readString(record, "display_name", "displayName"),
      allValues: readBoolean(record, "all_values", "allValues"),
      values,
    });
  }
  return items;
}

function normalizeObjectRef(raw: unknown): ImplementationObjectRef {
  const record = asRecord(raw);
  if (!record) {
    return {
      objectType: "",
      objectSubType: "",
      objectTypeName: "",
      objectSubTypeName: "",
      id: "",
      name: "",
      version: null,
    };
  }
  return {
    objectType: readString(record, "object_type", "objectType", "type"),
    objectSubType: readString(
      record,
      "object_sub_type",
      "objectSubType",
      "sub_type",
      "subtype",
    ),
    objectTypeName: readString(record, "object_type_name", "objectTypeName"),
    objectSubTypeName: readString(
      record,
      "object_sub_type_name",
      "objectSubTypeName",
    ),
    id: readString(record, "id"),
    name: readString(record, "name"),
    version: readStringOrNull(record, "version"),
  };
}

export function normalizeImplementationRecord(
  raw: unknown,
): ImplementationRecord | null {
  const record = asRecord(raw);
  if (!record) return null;
  const id = readString(record, "id");
  if (!id) return null;
  return {
    id,
    planId: readString(record, "plan_id", "planId"),
    assessmentSource: readString(record, "assessment_source", "assessmentSource"),
    assessmentName: readString(record, "assessment_name", "assessmentName"),
    assessor: readString(record, "assessor"),
    assessorName: readString(record, "assessor_name", "assessorName"),
    referenceObject: normalizeObjectRef(record.reference_object ?? record.referenceObject),
    impactedObject: normalizeObjectRef(record.impacted_object ?? record.impactedObject),
    impacted: readBoolean(record, "impacted"),
    impactedLevel: readStringOrNull(record, "impacted_level", "impactedLevel"),
    assessmentSummary: readStringOrNull(
      record,
      "assessment_summary",
      "assessmentSummary",
    ),
    detailedAnalysis: readStringOrNull(
      record,
      "detailed_analysis",
      "detailedAnalysis",
    ),
    recommendation: readStringOrNull(record, "recommendation"),
    assessmentRecordItems: normalizeAssessmentRecordItems(
      record.assessment_record_items ?? record.assessmentRecordItems,
    ),
    assessedAt: readString(record, "assessed_at", "assessedAt"),
  };
}

function normalizeImpactedType(raw: unknown): AssessmentPackImpactedType | null {
  const record = asRecord(raw);
  if (!record) return null;
  const objectType = readString(record, "object_type", "objectType");
  if (!objectType) return null;
  return {
    objectType,
    objectSubType: readString(record, "object_sub_type", "objectSubType"),
    displayName:
      readString(record, "object_type_name", "objectTypeName") ||
      readString(record, "display_name", "displayName") ||
      objectType,
  };
}

export function normalizeAssessmentPack(raw: unknown): AssessmentPack | null {
  const record = asRecord(raw);
  if (!record) return null;
  const referenceObject = normalizeObjectRef(
    record.reference_object ?? record.referenceObject,
  );
  if (!referenceObject.id || !referenceObject.objectType) return null;
  const typesRaw = record.impacted_object_types ?? record.impactedObjectTypes;
  const impactedObjectTypes = Array.isArray(typesRaw)
    ? typesRaw
        .map((item) => normalizeImpactedType(item))
        .filter((item): item is AssessmentPackImpactedType => item !== null)
    : [];
  const groupsRaw = record.impacted_user_groups ?? record.impactedUserGroups;
  const impactedUserGroups = Array.isArray(groupsRaw)
    ? groupsRaw
        .map((item) => (typeof item === "string" ? item.trim() : ""))
        .filter(Boolean)
    : [];
  return {
    name: readString(record, "name") || "Assessment Pack",
    referenceObject,
    impactedObjectTypes,
    impactedUserGroups,
  };
}

function normalizeGroup(raw: unknown): ImplementationRecordGroup | null {
  const record = asRecord(raw);
  if (!record) return null;
  const assessmentName = readString(record, "assessment_name", "assessmentName");
  if (!assessmentName) return null;
  return {
    assessmentName,
    assessmentSource: readString(record, "assessment_source", "assessmentSource"),
    recordCount: readNumber(record, "record_count", "recordCount") ?? 0,
  };
}

function assignScalarParam(
  params: Record<string, string | number | boolean | string[]>,
  key: string,
  value?: ImplementationQueryScalar,
) {
  const list = queryScalarList(value);
  if (list.length === 1) {
    params[key] = list[0];
    return;
  }
  if (list.length > 1) {
    params[key] = list;
  }
}

function toQueryParams(
  query: ImplementationRecordListQuery,
): Record<string, string | number | boolean | string[]> {
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? IMPLEMENTATION_RECORDS_DEFAULT_PAGE_SIZE;
  const params: Record<string, string | number | boolean | string[]> = {
    page,
    page_size: pageSize,
  };
  assignScalarParam(params, "plan_id", query.planId);
  assignScalarParam(params, "assessment_source", query.assessmentSource);
  assignScalarParam(params, "assessment_name", query.assessmentName);
  assignScalarParam(params, "reference_object_type", query.referenceObjectType);
  if (query.referenceObjectId?.trim()) {
    params.reference_object_id = query.referenceObjectId.trim();
  }
  if (query.referenceObjectVersion?.trim()) {
    params.reference_object_version = query.referenceObjectVersion.trim();
  }
  const impactedValues = queryBooleanList(query.impacted);
  if (impactedValues.length === 1) {
    params.impacted = impactedValues[0];
  }
  assignScalarParam(params, "impacted_object_type", query.impactedObjectType);
  if (query.impactedObjectId?.trim()) {
    params.impacted_object_id = query.impactedObjectId.trim();
  }
  if (query.keyword?.trim()) params.keyword = query.keyword.trim();
  if (query.assessedFrom?.trim()) params.assessed_from = query.assessedFrom.trim();
  if (query.assessedTo?.trim()) params.assessed_to = query.assessedTo.trim();
  return params;
}

function readPaginatedEnvelope(
  result: { data?: unknown; total?: number; page?: number; page_size?: number },
  fallbackPage: number,
  fallbackPageSize: number,
): ImplementationPaginatedResult<ImplementationRecord> {
  const extracted = extractListPayload(result.data);
  const items = extracted.items
    .map((item) => normalizeImplementationRecord(item))
    .filter((item): item is ImplementationRecord => item !== null);
  return {
    items,
    total: result.total ?? extracted.total ?? items.length,
    page: result.page ?? extracted.page ?? fallbackPage,
    pageSize: result.page_size ?? extracted.pageSize ?? fallbackPageSize,
  };
}

function readImpactedItemFilterOptions(raw: unknown): ImplementationFilterOption[] {
  if (!Array.isArray(raw)) return [];
  const options: ImplementationFilterOption[] = [];
  for (const item of raw) {
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
      const value = String(item).trim();
      if (value) options.push({ value, label: value });
      continue;
    }
    const record = asRecord(item);
    if (!record) continue;
    const objectType = readString(record, "object_type", "objectType", "value", "code", "id");
    if (!objectType) continue;
    const label =
      readString(record, "object_type_name", "objectTypeName", "label", "name") ||
      objectType;
    options.push({ value: objectType, label });
  }
  return options;
}

function readFilterOptionList(raw: unknown): ImplementationFilterOption[] {
  if (!Array.isArray(raw)) return [];
  const options: ImplementationFilterOption[] = [];
  for (const item of raw) {
    if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
      const value = String(item).trim();
      if (value) options.push({ value, label: value });
      continue;
    }
    const record = asRecord(item);
    if (!record) continue;
    const value = readString(
      record,
      "value",
      "code",
      "id",
      "object_type",
      "objectType",
    );
    if (!value) continue;
    const label =
      readString(record, "label", "name", "display_name", "displayName") || value;
    options.push({ value, label });
  }
  return options;
}

function pickFilterList(
  record: Record<string, unknown>,
  ...keys: string[]
): unknown {
  for (const key of keys) {
    if (record[key] != null) return record[key];
  }
  const nested = asRecord(record.data);
  if (!nested) return undefined;
  for (const key of keys) {
    if (nested[key] != null) return nested[key];
  }
  return undefined;
}

export function normalizeImplementationRecordFilterOptions(
  raw: unknown,
): ImplementationRecordFilterOptions {
  const record = asRecord(raw);
  if (!record) return { ...EMPTY_IMPLEMENTATION_RECORD_FILTER_OPTIONS };
  return {
    assessmentSources: readFilterOptionList(
      pickFilterList(
        record,
        "assessment_sources",
        "assessmentSources",
        "assessment_source",
      ),
    ),
    assessmentNames: readFilterOptionList(
      pickFilterList(record, "assessment_names", "assessmentNames"),
    ),
    impactedObjectTypes: readImpactedItemFilterOptions(
      pickFilterList(record, "impacted_object_types", "impactedObjectTypes"),
    ),
  };
}

export async function fetchImplementationRecordFilterOptions(): Promise<ImplementationRecordFilterOptions> {
  const result = await apiClient.get<unknown>(
    "/implementation-records/filter-options",
  );
  assertSuccess(result);
  return normalizeImplementationRecordFilterOptions(result.data);
}

export async function fetchImplementationRecords(
  query: ImplementationRecordListQuery = {},
): Promise<ImplementationPaginatedResult<ImplementationRecord>> {
  if (implementationRecordConfig.useListMock) {
    return mockFetchImplementationRecords(query);
  }
  const page = query.page ?? 1;
  const pageSize = query.pageSize ?? IMPLEMENTATION_RECORDS_DEFAULT_PAGE_SIZE;
  const result = await apiClient.get<unknown>(
    "/implementation-records",
    toQueryParams(query),
  );
  assertSuccess(result);
  return readPaginatedEnvelope(
    result as {
      data?: unknown;
      total?: number;
      page?: number;
      page_size?: number;
    },
    page,
    pageSize,
  );
}

export async function fetchAllImplementationRecordsForPack(
  query: Omit<ImplementationRecordListQuery, "page" | "pageSize">,
): Promise<ImplementationRecord[]> {
  const first = await fetchImplementationRecords({
    ...query,
    page: 1,
    pageSize: IMPLEMENTATION_RECORDS_PACK_PAGE_SIZE,
  });
  const items = [...first.items];
  const totalPages = Math.min(
    Math.ceil(first.total / IMPLEMENTATION_RECORDS_PACK_PAGE_SIZE) || 1,
    IMPLEMENTATION_RECORDS_PACK_MAX_PAGES,
  );
  for (let page = 2; page <= totalPages; page += 1) {
    const next = await fetchImplementationRecords({
      ...query,
      page,
      pageSize: IMPLEMENTATION_RECORDS_PACK_PAGE_SIZE,
    });
    items.push(...next.items);
  }
  return items;
}

export async function fetchAssessmentPackDetail(query: {
  referenceObjectType: string;
  referenceObjectId: string;
  referenceObjectVersion?: string;
}): Promise<AssessmentPack> {
  if (implementationRecordConfig.useMock) {
    return mockFetchAssessmentPackDetail(query);
  }
  const params: Record<string, string> = {
    reference_object_type: query.referenceObjectType.trim(),
    reference_object_id: query.referenceObjectId.trim(),
  };
  if (query.referenceObjectVersion?.trim()) {
    params.reference_object_version = query.referenceObjectVersion.trim();
  }
  const result = await apiClient.get<unknown>("/assessment-packs/detail", params);
  assertSuccess(result);
  const pack = normalizeAssessmentPack(result.data);
  if (!pack) {
    throw new Error("Assessment pack not found.");
  }
  return pack;
}

export async function fetchImplementationRecordGroups(query: {
  referenceObjectType: string;
  referenceObjectId: string;
  referenceObjectVersion?: string;
  keyword?: string;
  sortOrder?: "asc" | "desc";
}): Promise<ImplementationRecordGroup[]> {
  if (implementationRecordConfig.useMock) {
    return mockFetchImplementationRecordGroups(query);
  }
  const params: Record<string, string> = {
    reference_object_type: query.referenceObjectType.trim(),
    reference_object_id: query.referenceObjectId.trim(),
  };
  if (query.referenceObjectVersion?.trim()) {
    params.reference_object_version = query.referenceObjectVersion.trim();
  }
  if (query.keyword?.trim()) params.keyword = query.keyword.trim();
  if (query.sortOrder) params.sort_order = query.sortOrder;
  const result = await apiClient.get<unknown>(
    "/implementation-records/groups",
    params,
  );
  assertSuccess(result);
  const extracted = extractListPayload(result.data);
  const rawGroups = extracted.groups ?? extracted.items;
  return rawGroups
    .map((item) => normalizeGroup(item))
    .filter((item): item is ImplementationRecordGroup => item !== null);
}
